/**
 * LinkedAccountsDisclosure — the device/IP correlation drill-down for ONE account.
 *
 * This read is AUDITED: `GET /admin/users/{id}/linked-accounts` writes an `audit_log` row against
 * the signed-in staff member, because it is a targeted read of a named person's correlation graph.
 * So it never fires on render or hover — the query lives in a child that mounts ONLY while the panel
 * is open, so opening the panel IS the deliberate act that reads. That mirrors the console's rule
 * for every consequential read: a human asks for it, on purpose, and their id is on the record.
 *
 * *** WHY THIS QUERY OVERRIDES THE CLIENT DEFAULTS ***
 * The app's QueryClient is tuned for a live queue: `refetchOnWindowFocus: true`, `staleTime: 0`.
 * Inherited here, that would re-hit an AUDITED endpoint every time the reviewer alt-tabs back with
 * the panel open — a phantom audit row and a re-pull of PII that no human deliberately asked for.
 * So this query pins `refetchOnWindowFocus: false`, `refetchOnReconnect: false`, `staleTime:
 * Infinity`: once opened, it reads once and does not read again on its own. `gcTime: 0` (plus the
 * mount-on-open structure) drops the cached graph the instant the panel closes, so nothing lingers
 * in memory and a genuine re-open is one fresh, deliberate audited read — never a silent cache hit.
 *
 * The response is not paged (`{ userId, items }`, no total) — the server orders it strongest-first
 * and bounds it, and the console renders exactly what it is handed. An unknown id and an id with no
 * links both come back empty: the same "nothing here", which is why probing an id tells you nothing.
 *
 * The collapsed region stays in the DOM (`hidden`) so the trigger's `aria-controls` always resolves
 * to a real element; its contents mount only while open, so the audited query cannot run behind a
 * closed panel.
 */
import { useId, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchLinkedAccounts } from '@/lib/admin-api';
import { formatTimestamp, type LinkedAccount } from '@/lib/wire';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

interface LinkedAccountsDisclosureProps {
  userId: string;
  /** A short, honest note on WHOSE graph this is, e.g. "This candidate's account." */
  note?: string;
}

export function LinkedAccountsDisclosure({ userId, note }: LinkedAccountsDisclosureProps) {
  const [open, setOpen] = useState(false);
  const regionId = useId();

  return (
    <div>
      <Button
        variant="ghost"
        size="sm"
        aria-expanded={open}
        aria-controls={regionId}
        onClick={() => setOpen((value) => !value)}
      >
        {open ? 'Hide linked accounts' : 'Linked accounts'}
      </Button>

      {/* The region container stays mounted (`hidden`) so `aria-controls` always resolves; its
          contents — and the audited query inside them — mount only while open. */}
      <div id={regionId} hidden={!open} className="mt-2.5">
        {open && <LinkedAccountsRegion userId={userId} note={note} />}
      </div>
    </div>
  );
}

function LinkedAccountsRegion({ userId, note }: LinkedAccountsDisclosureProps) {
  const linked = useQuery({
    queryKey: ['linked-accounts', userId],
    queryFn: () => fetchLinkedAccounts(userId),
    // See the module header: an audited read must not repeat itself on window focus or reconnect,
    // and must not linger in cache once the panel is closed.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    gcTime: 0,
  });

  return (
    <div className="rounded-md border border-line-2 bg-paper-2 p-3.5">
      <p className="font-body text-[11.5px] leading-relaxed text-ink-70">
        Accounts that share a sign-in IP or a registered device with{' '}
        {note ? note.replace(/\.$/, '') : 'this account'}, strongest signal first. A signal for a
        human to weigh &mdash; a shared IP is usually a household, an office or a carrier NAT. Looking
        this up is recorded against your staff id.
      </p>

      {linked.isPending && (
        <p role="status" className="mt-3 font-body text-[12px] font-medium text-ink-70">
          Loading linked accounts&hellip;
        </p>
      )}

      {linked.isError && (
        <div role="alert" className="mt-3">
          <p className="border-l-2 border-coral-deep pl-2.5 font-body text-[12px] font-medium leading-relaxed text-ink">
            Couldn&rsquo;t load linked accounts. Try again in a moment.
          </p>
          <div className="mt-2">
            <Button variant="ghost" size="sm" onClick={() => linked.refetch()}>
              Try again
            </Button>
          </div>
        </div>
      )}

      {linked.isSuccess &&
        (linked.data.items.length === 0 ? (
          <p className="mt-3 font-body text-[12.5px] font-medium text-ink">
            No other account shares a sign-in IP or device with this one.
          </p>
        ) : (
          <>
            <ul className="mt-3 flex flex-col gap-2">
              {linked.data.items.map((account) => (
                <LinkedAccountRow key={account.userId} account={account} />
              ))}
            </ul>
            {/* The server caps this list; `truncated` is how it tells us it did. Say so plainly, so
                a reviewer weighs "the first N of more" rather than mistaking N for the whole graph.
                The count comes from what we were handed, not a hard-coded cap, so it stays honest if
                the server's limit ever changes. */}
            {linked.data.truncated && (
              <p className="mt-2.5 font-body text-[11.5px] leading-relaxed text-ink-70">
                Showing the first {linked.data.items.length} linked accounts &mdash; more share a
                sign-in IP or device than are listed here.
              </p>
            )}
          </>
        ))}
    </div>
  );
}

function LinkedAccountRow({ account }: { account: LinkedAccount }) {
  const ips = account.sharedIpCount;
  const devices = account.sharedDeviceCount;
  return (
    <li className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-line bg-surface px-3.5 py-2.5">
      <div className="min-w-0">
        <p className="font-display text-[12.5px] font-medium text-ink">{account.displayName}</p>
        <p className="mt-0.5 font-body text-[11px] text-ink-70">
          <span className="font-mono text-[10.5px]">{account.userId}</span> &middot; created{' '}
          {formatTimestamp(account.createdAt)}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Badge tone="neutral">{account.status}</Badge>
        {ips > 0 && (
          <Badge tone="neutral">
            {ips} shared {ips === 1 ? 'IP' : 'IPs'}
          </Badge>
        )}
        {devices > 0 && (
          <Badge tone="coral">
            {devices} shared {devices === 1 ? 'device' : 'devices'}
          </Badge>
        )}
      </div>
    </li>
  );
}
